import { cache } from "react";
import { isSafeHttpUrl } from "@/lib/routes";

const DEFAULT_ACCEPT_VERSION = "v6.0";

// Inlined from @/lib/api/cache — Turbopack drops the `.next` property from
// re-exported `as const` objects, causing a runtime crash when accessing
// `LONG_CACHE.next.revalidate`.  Using plain numbers avoids the bundler issue.
const GHOST_REVALIDATE_LONG = 300;
const GHOST_REVALIDATE_SHORT = 60;

export interface GhostTag {
  id: string;
  name: string;
  slug: string;
  description: string | null;
}

export interface GhostAuthor {
  id: string;
  name: string;
  slug: string;
  bio: string | null;
  profile_image: string | null;
  twitter: string | null;
  facebook: string | null;
  website: string | null;
  count?: { posts: number };
}

/**
 * Build a `sameAs` array for JSON-LD Person schema from Ghost author social fields.
 * Converts Twitter handles to full URLs, passes through Facebook and website as-is.
 */
export function buildAuthorSameAs(author: GhostAuthor): string[] {
  const links: string[] = [];

  if (author.twitter) {
    const handle = author.twitter.replace(/^@/, "");
    links.push(`https://x.com/${handle}`);
  }

  const fb = resolveFacebookUrl(author.facebook);
  if (fb) {
    links.push(fb);
  }

  if (isSafeHttpUrl(author.website)) {
    links.push(author.website);
  }

  return links;
}

/** Resolve a Ghost `author.facebook` field to a safe http(s) URL or null.
 *  Validates the constructed URL with `isSafeHttpUrl` so a malicious raw
 *  value like `javascript:` doesn't slip through the username branch. */
function resolveFacebookUrl(raw: string | undefined | null): string | null {
  if (!raw) return null;
  const candidate = raw.startsWith("http") ? raw : `https://www.facebook.com/${raw}`;
  return isSafeHttpUrl(candidate) ? candidate : null;
}

export interface AuthorSocialLink {
  url: string;
  label: string;
}

/**
 * Build a structured list of social links for a Ghost author.
 * Normalises Twitter handles to full URLs and Facebook usernames to profile URLs.
 * Only includes the website link if it passes `isSafeHttpUrl`.
 */
export function getAuthorSocialLinks(author: GhostAuthor): AuthorSocialLink[] {
  const links: AuthorSocialLink[] = [];

  if (author.twitter) {
    const handle = author.twitter.replace(/^@/, "");
    links.push({ url: `https://x.com/${handle}`, label: `@${handle} on X` });
  }

  const fbUrl = resolveFacebookUrl(author.facebook);
  if (fbUrl) {
    links.push({ url: fbUrl, label: "Facebook" });
  }

  if (isSafeHttpUrl(author.website)) {
    links.push({ url: author.website, label: "Website" });
  }

  return links;
}

export interface GhostPost {
  id: string;
  uuid: string;
  slug: string;
  title: string;
  html: string;
  excerpt: string | null;
  feature_image: string | null;
  feature_image_alt: string | null;
  canonical_url: string | null;
  url: string | null;
  published_at: string;
  updated_at: string;
  reading_time: number;
  custom_excerpt: string | null;
  meta_title: string | null;
  meta_description: string | null;
  og_image: string | null;
  og_description: string | null;
  og_title: string | null;
  twitter_image: string | null;
  twitter_title: string | null;
  twitter_description: string | null;
  primary_author: GhostAuthor | null;
  authors: GhostAuthor[];
  tags: GhostTag[];
}

export interface GhostPage {
  id: string;
  uuid: string;
  slug: string;
  title: string;
  html: string;
  excerpt: string | null;
  feature_image: string | null;
  canonical_url: string | null;
  url: string | null;
  published_at: string;
  updated_at: string;
  custom_excerpt: string | null;
  meta_title: string | null;
  meta_description: string | null;
  tags: GhostTag[];
}

interface GhostPostsResponse {
  posts: GhostPost[];
  meta?: {
    pagination?: {
      page: number;
      limit: number;
      pages: number;
      total: number;
      next: number | null;
      prev: number | null;
    };
  };
}

interface GhostPagesResponse {
  pages: GhostPage[];
}

function getGhostConfig() {
  const apiUrl = process.env.GHOST_API_URL?.trim().replace(/\/+$/, "");
  const contentApiKey = process.env.GHOST_CONTENT_API_KEY?.trim();
  const acceptVersion = process.env.GHOST_CONTENT_API_VERSION?.trim() || DEFAULT_ACCEPT_VERSION;

  return {
    apiUrl,
    contentApiKey,
    acceptVersion,
    isConfigured: Boolean(apiUrl && contentApiKey),
  };
}

type QueryParams = Record<string, string | number | undefined>;

function buildGhostUrl(resource: string, params: QueryParams) {
  const { apiUrl, contentApiKey, isConfigured } = getGhostConfig();

  if (!isConfigured || !apiUrl || !contentApiKey) {
    throw new Error("Ghost is not configured. Set GHOST_API_URL and GHOST_CONTENT_API_KEY.");
  }

  const url = new URL(`/ghost/api/content/${resource}/`, apiUrl);
  url.searchParams.set("key", contentApiKey);
  url.searchParams.set("include", "tags,authors");
  url.searchParams.set("formats", "html");
  url.searchParams.set("limit", "all");
  url.searchParams.set("order", "published_at desc");

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      url.searchParams.set(key, String(value));
    }
  }

  return url;
}

async function ghostFetchPosts(
  resource: string,
  params: QueryParams = {},
  revalidate: number = GHOST_REVALIDATE_LONG,
): Promise<GhostPostsResponse> {
  const url = buildGhostUrl(resource, params);
  const { acceptVersion } = getGhostConfig();
  const response = await fetch(url, {
    headers: { "Accept-Version": acceptVersion },
    next: { revalidate, tags: ["ghost-posts"] },
  });

  if (!response.ok) {
    throw new Error(`Ghost request failed (${response.status}) for ${url.pathname}`);
  }
  return response.json() as Promise<GhostPostsResponse>;
}

async function ghostFetchPages(resource: string, params: QueryParams = {}): Promise<GhostPagesResponse> {
  const url = buildGhostUrl(resource, params);
  const { acceptVersion } = getGhostConfig();
  const response = await fetch(url, {
    headers: { "Accept-Version": acceptVersion },
    next: { revalidate: GHOST_REVALIDATE_LONG, tags: ["ghost-pages"] },
  });

  if (!response.ok) {
    throw new Error(`Ghost request failed (${response.status}) for ${url.pathname}`);
  }
  return response.json() as Promise<GhostPagesResponse>;
}

interface GhostAuthorsResponse {
  authors: GhostAuthor[];
}

async function ghostFetchAuthors(resource: string, params: QueryParams = {}): Promise<GhostAuthorsResponse> {
  const { apiUrl, contentApiKey, isConfigured, acceptVersion } = getGhostConfig();

  if (!isConfigured || !apiUrl || !contentApiKey) {
    throw new Error("Ghost is not configured. Set GHOST_API_URL and GHOST_CONTENT_API_KEY.");
  }

  const url = new URL(`/ghost/api/content/${resource}/`, apiUrl);
  url.searchParams.set("key", contentApiKey);
  url.searchParams.set("limit", "all");

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      url.searchParams.set(key, String(value));
    }
  }

  const response = await fetch(url, {
    headers: { "Accept-Version": acceptVersion },
    next: { revalidate: GHOST_REVALIDATE_LONG, tags: ["ghost-authors"] },
  });

  if (!response.ok) {
    throw new Error(`Ghost request failed (${response.status}) for ${url.pathname}`);
  }
  return response.json() as Promise<GhostAuthorsResponse>;
}

export const isGhostConfigured = () => getGhostConfig().isConfigured;

export const getGhostPosts = cache(async (limit?: number) => {
  // Exclude banner posts — they're tagged #banner-active and are used as
  // runtime announcements, not blog articles.
  const params: QueryParams = { filter: "tag:-hash-banner-active" };
  if (limit) params.limit = limit;
  const response = await ghostFetchPosts("posts", params);
  return response.posts;
});

export const getGhostPostBySlug = cache(async (slug: string) => {
  const response = await ghostFetchPosts("posts/slug/" + encodeURIComponent(slug));
  return response.posts[0] ?? null;
});

/**
 * Fetches a Ghost *page* (not post) by slug. Used for static content
 * such as privacy, terms, about.
 */
export const getGhostPage = cache(async (slug: string): Promise<GhostPage | null> => {
  try {
    const response = await ghostFetchPages("pages/slug/" + encodeURIComponent(slug));
    return response.pages[0] ?? null;
  } catch (error) {
    console.error(`[ghost] Failed to load page "${slug}"`, error);
    return null;
  }
});

/**
 * Fetches posts with a specific internal tag. Ghost internal tags start
 * with `#` — URL-encoded they become `hash-*`.
 */
export const getGhostPostsByTag = cache(async (tag: string, limit?: number): Promise<GhostPost[]> => {
  try {
    const params: QueryParams = { filter: `tag:${tag}` };
    if (limit) params.limit = limit;
    // Banner/status tags need fresher data than regular content fetches.
    const revalidate =
      tag.startsWith("hash-banner") || tag.startsWith("hash-status") ? GHOST_REVALIDATE_SHORT : GHOST_REVALIDATE_LONG;
    const response = await ghostFetchPosts("posts", params, revalidate);
    return response.posts;
  } catch (error) {
    console.error(`[ghost] Failed to load posts for tag "${tag}"`, error);
    return [];
  }
});

export const getGhostAuthors = cache(async (): Promise<GhostAuthor[]> => {
  const response = await ghostFetchAuthors("authors", { include: "count.posts" });
  return response.authors;
});

export const getGhostAuthorBySlug = cache(async (slug: string): Promise<GhostAuthor | null> => {
  try {
    const response = await ghostFetchAuthors("authors/slug/" + encodeURIComponent(slug), {
      include: "count.posts",
    });
    return response.authors[0] ?? null;
  } catch (error) {
    console.error(`[ghost] Failed to load author "${slug}"`, error);
    return null;
  }
});

export const getGhostPostsByAuthor = cache(async (slug: string): Promise<GhostPost[]> => {
  try {
    const response = await ghostFetchPosts("posts", {
      filter: `author:${slug}+tag:-hash-banner-active`,
    });
    return response.posts;
  } catch (error) {
    console.error(`[ghost] Failed to load posts for author "${slug}"`, error);
    return [];
  }
});

export type BannerSeverity = "info" | "warn" | "critical";
/**
 * Surface a banner targets. `null` = render everywhere (default).
 * - `marketing` → public (logged-out) routes under `app/(public)/*`
 * - `app`       → authed dashboard routes under `app/(v2)/*`
 * Tag one of `#scope-marketing` / `#scope-app` on the Ghost post to scope it.
 */
export type BannerScope = "marketing" | "app";

export interface GhostBanner {
  id: string;
  slug: string;
  title: string;
  message: string;
  html: string;
  severity: BannerSeverity;
  /** Slug of the integration/connector this banner targets, or null for site-wide. */
  scopeSlug: string | null;
  /** Surface this banner targets — marketing, app, or both (null). */
  scope: BannerScope | null;
  ctaLabel: string | null;
  ctaUrl: string | null;
  publishedAt: string;
  updatedAt: string;
}

const BANNER_ACTIVE_TAG = "hash-banner-active";
const BANNER_SEVERITY_TAGS: Record<string, BannerSeverity> = {
  "hash-banner-info": "info",
  "hash-banner-warn": "warn",
  "hash-banner-warning": "warn",
  "hash-banner-critical": "critical",
};
const BANNER_SCOPE_TAGS: Record<string, BannerScope> = {
  "hash-scope-marketing": "marketing",
  "hash-scope-app": "app",
};
const STATUS_TAG_PREFIX = "hash-status-";

function parseBanner(post: GhostPost): GhostBanner {
  let severity: BannerSeverity = "info";
  let scopeSlug: string | null = null;
  let scope: BannerScope | null = null;

  for (const tag of post.tags) {
    if (BANNER_SEVERITY_TAGS[tag.slug]) severity = BANNER_SEVERITY_TAGS[tag.slug];
    if (BANNER_SCOPE_TAGS[tag.slug]) scope = BANNER_SCOPE_TAGS[tag.slug];
    if (tag.slug.startsWith(STATUS_TAG_PREFIX)) {
      scopeSlug = tag.slug.slice(STATUS_TAG_PREFIX.length);
    }
  }

  const message = post.custom_excerpt || post.excerpt || "";

  return {
    id: post.id,
    slug: post.slug,
    title: post.title,
    message,
    html: post.html,
    severity,
    scopeSlug,
    scope,
    ctaLabel: post.og_title, // reuse og_title for CTA label convention
    ctaUrl: post.canonical_url, // reuse canonical_url for CTA url convention
    publishedAt: post.published_at,
    updatedAt: post.updated_at,
  };
}

/**
 * Fetches all currently active banners — posts tagged with `#banner-active`.
 * Returns both site-wide and scoped banners; filter in the render layer.
 */
export const getActiveBanners = cache(async (): Promise<GhostBanner[]> => {
  if (!isGhostConfigured()) return [];
  const posts = await getGhostPostsByTag(BANNER_ACTIVE_TAG);
  return posts.map(parseBanner);
});

/**
 * Banners with no `#status-*` tag — shown across the whole site.
 * If `surface` is provided, also filters to banners whose `scope` is either
 * that surface or null (unscoped banners render on every surface).
 */
export const getSiteWideBanners = cache(async (surface?: BannerScope): Promise<GhostBanner[]> => {
  const all = await getActiveBanners();
  return all.filter(b => {
    if (b.scopeSlug !== null) return false;
    if (!surface) return true;
    return b.scope === null || b.scope === surface;
  });
});

/** Banners scoped to a specific integration slug via `#status-<slug>`. */
export const getBannersForScope = cache(async (slug: string): Promise<GhostBanner[]> => {
  const all = await getActiveBanners();
  return all.filter(b => b.scopeSlug === slug);
});

export function getGhostOrigin() {
  return getGhostConfig().apiUrl;
}

export function getGhostAdminUrl() {
  const origin = getGhostOrigin();
  return origin ? `${origin}/ghost/` : null;
}

export function getGhostPostExcerpt(post: GhostPost) {
  return post.custom_excerpt || post.excerpt || "";
}

function escapeHtmlAttribute(value: string) {
  return value.replace(/"/g, "&quot;");
}

export function prepareGhostHtmlForRender(html: string) {
  return html.replace(
    /(<figure\b[^>]*class="[^"]*\bkg-video-card\b[^"]*"[^>]*\bdata-kg-thumbnail="([^"]*)"[^>]*>[\s\S]*?<video\b)([^>]*)(><\/video>)/g,
    (_match, videoPrefix: string, thumbnail: string, videoAttrs: string, videoSuffix: string) => {
      let nextAttrs = videoAttrs;

      if (!/\scontrols(?:[\s=>]|$)/.test(nextAttrs)) {
        nextAttrs += " controls";
      }

      if (thumbnail) {
        const poster = ` poster="${escapeHtmlAttribute(thumbnail)}"`;
        nextAttrs = /\sposter=("[^"]*"|'[^']*')/.test(nextAttrs)
          ? nextAttrs.replace(/\sposter=("[^"]*"|'[^']*')/, poster)
          : `${nextAttrs}${poster}`;
      }

      return `${videoPrefix}${nextAttrs}${videoSuffix}`;
    },
  );
}
